import { pricingPlansModalMessages } from '@/features/settings/billing/components/pricing-plans-modal/pricing-plans-modal.messages';
import { cn } from '@/lib/cn';
import { Badge } from '@heidi/design-system/badge';
import { TierPracticeFilled, TierProFilled } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyH2,
  TypographyH3,
} from '@heidi/design-system/typography';
import { ExternalLink } from 'lucide-react';
import { memo, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

type IconComponent = React.ComponentType<{ size?: number | string; className?: string }>;

/**
 * Card chrome for the pricing plans modal. The base keeps its older chrome for the
 * trial-end modal, which shares this component but not this design.
 */
export const PRICING_PLANS_MODAL_CARD_CLASSES =
  'size-full min-h-0 min-w-0 gap-6 rounded-xl border';

const BADGE_POSITION_CLASSES =
  'absolute left-1/4 top-0 w-fit -translate-x-3/4 -translate-y-1/2 p-1';

export type PricingPlanCardFeature = {
  icon: IconComponent;
  description?: string;
};

export type PricingPlanCardProps = {
  /** Extra classes on the card root (e.g. `h-full min-h-0` for grid layouts). */
  className?: string;
  isCurrentPlan?: boolean;
  isMostPopular?: boolean;
  /** Emphasised card — border in its own tier tone instead of the neutral one. */
  isHighlighted?: boolean;
  /** Selects the Practice vs Pro tier family for the highlight border and "Current" badge. */
  isClinicianProPlusTier?: boolean;
  planTitle: ReactNode;
  planSubtitle?: ReactNode;
  /** Renders above the title in a full-width row (e.g. period tabs, optionally with a start-aligned save badge). */
  periodToggle?: ReactNode;
  /** Price row via `priceSlot` (`PlanPriceDisplay`) or `priceText` + captions. */
  priceSlot?: ReactNode;
  priceText?: ReactNode;
  priceSubtitles?: ReactNode;
  cta: ReactNode;
  isCtaDisabled?: boolean;
  ctaDisabledTooltip?: string;
  features: PricingPlanCardFeature[];
  featuresSectionTitle?: ReactNode;
  seeMoreHref?: string;
  /** Renders below the feature list (e.g. the Practice "Available roles" section). */
  footer?: ReactNode;
};

function PricingPlanCardComponent({
  className,
  isCurrentPlan,
  isMostPopular,
  isHighlighted,
  isClinicianProPlusTier,
  planTitle,
  planSubtitle,
  periodToggle,
  priceSlot,
  priceText,
  priceSubtitles,
  cta,
  isCtaDisabled,
  ctaDisabledTooltip,
  features,
  featuresSectionTitle,
  seeMoreHref,
  footer,
}: PricingPlanCardProps) {
  return (
    <div
      className={cn(
        'relative flex grow flex-col gap-4 rounded-lg border-2 border-border bg-surface p-6 newBrandDark:bg-surface-elevated',
        isHighlighted &&
          (isClinicianProPlusTier
            ? 'border-tier-practice-border'
            : 'border-tier-pro-border'),
        isCurrentPlan && 'border-border-selected',
        className
      )}
    >
      {isCurrentPlan ? (
        <Badge
          color={isClinicianProPlusTier ? 'neutral' : 'proTier'}
          size="xs"
          className={cn(
            BADGE_POSITION_CLASSES,
            'gap-1',
            isClinicianProPlusTier &&
              'bg-tier-practice-muted text-tier-practice-text shadow-none'
          )}
        >
          {isClinicianProPlusTier ? (
            <TierPracticeFilled size={10} />
          ) : (
            <TierProFilled size={10} />
          )}
          <FormattedMessage {...pricingPlansModalMessages.current} />
        </Badge>
      ) : isMostPopular ? (
        <Badge
          color="neutral"
          size="xs"
          className={cn(
            BADGE_POSITION_CLASSES,
            'bg-background-primary text-text-primary-invert'
          )}
        >
          <FormattedMessage {...pricingPlansModalMessages.mostPopular} />
        </Badge>
      ) : null}
      <div className="flex shrink-0 flex-col gap-2">
        {/* min-h-6 is the sm segmented control's own height, so the row
            reserves space for it without padding the design's rhythm. */}
        {!!periodToggle && <div className="flex min-h-6 w-full">{periodToggle}</div>}

        <div className="flex flex-col gap-1">
          <TypographyH3 className="min-w-0 text-text-primary">{planTitle}</TypographyH3>
          {!!planSubtitle && (
            <TypographyCaption className="min-w-0 text-text-secondary">
              {planSubtitle}
            </TypographyCaption>
          )}
        </div>

        {priceSlot ?? (
          <div className="flex items-start gap-2">
            <TypographyH2 className="text-text-secondary">{priceText}</TypographyH2>
            {priceSubtitles}
          </div>
        )}

        {/* Part of the plan-name block in the design, so it takes that
            block's 8px rhythm rather than the card's section gap. */}
        <div className="w-full">
          {isCtaDisabled && ctaDisabledTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block w-full">{cta}</span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{ctaDisabledTooltip}</TooltipContent>
            </Tooltip>
          ) : (
            cta
          )}
        </div>
      </div>

      {/*
        Only the section under the CTA scrolls once the card's content outgrows
        the height the row gives it — the Practice card's "Available roles", in
        practice — so the plan name, price and upgrade button stay put.
        `gap-[inherit]` keeps the root's gap authoritative, so `className` still
        sets the card's section spacing. `overflow-x-hidden` because `overflow-y`
        alone computes the other axis to `auto`, which scrolls on any stray
        sub-pixel width.
      */}
      <div className="flex min-h-0 flex-1 flex-col gap-[inherit] overflow-y-auto overflow-x-hidden scroll-smooth scrollbar-none motion-reduce:scroll-auto">
        {/*
          No `min-h-0`: the list grows into the card's spare height to keep the
          footer at the bottom, but stops shrinking at its own content, so a
          footer that outgrows the card overflows the scroll box instead.
        */}
        <div className="flex flex-1 flex-col gap-2">
          {featuresSectionTitle && (
            <TypographyCaption className="font-medium text-text-primary">
              {featuresSectionTitle}
            </TypographyCaption>
          )}
          <ul className="flex flex-col gap-2" role="list">
            {features.map(({ description, icon: Icon }, index) => (
              <li key={description ?? index} className="flex items-center gap-2">
                <Icon className="size-4 shrink-0 text-text-secondary" aria-hidden />
                <TypographyCaption className="min-w-0 text-text-primary">
                  {description}
                </TypographyCaption>
              </li>
            ))}
          </ul>
        </div>

        {seeMoreHref && (
          <Link
            href={seeMoreHref}
            target="_blank"
            rel="noopener noreferrer"
            variant="standalone"
            size="sm"
          >
            <FormattedMessage {...pricingPlansModalMessages.seeMore} />
            <ExternalLink className="size-3" aria-hidden />
          </Link>
        )}

        {footer}
      </div>
    </div>
  );
}

export const PricingPlanCardBase = memo(PricingPlanCardComponent);
